/**
 * MULTI-JURISDICTION P6 — PendingJurisdictionApplier.
 *
 * A new ORGANIZATION picks its home zone on the signup page (unauthenticated, no
 * tenant yet), so the choice is stashed in localStorage. The signup backend is not
 * changed. On the FIRST authenticated load this null-rendering helper applies the
 * stored zone to the freshly-created tenant via the already-live P2 endpoint
 * `PUT /tenants/{id}/jurisdictions`, then clears the key and refreshes the live
 * JurisdictionProvider so currency/locale/terminology switch immediately.
 *
 * Best-effort and idempotent: on any error it leaves the key for a later retry;
 * for an existing user with no pending key it does nothing (no extra network).
 */
import { useEffect, useRef } from 'react'
import apiClient from '../api/client'
import { resolveApiUrl } from '../api/config'
import { useTenantId } from '../auth/useAuth'
import { useJurisdiction } from './JurisdictionProvider'
import { PENDING_JURISDICTION_KEY } from './pending'

export function PendingJurisdictionApplier() {
  const tenantId = useTenantId()
  const { refetch } = useJurisdiction()
  const applied = useRef(false)

  useEffect(() => {
    if (applied.current) return
    if (!tenantId || tenantId === 'default-tenant') return

    let pending: string | null = null
    try {
      pending = window.localStorage.getItem(PENDING_JURISDICTION_KEY)
    } catch {
      return
    }
    if (!pending) return

    applied.current = true
    const code = pending
    apiClient
      .put(resolveApiUrl(`/tenants/${tenantId}/jurisdictions`), {
        jurisdictions: [code],
        primary_jurisdiction: code,
      })
      .then(() => {
        try { window.localStorage.removeItem(PENDING_JURISDICTION_KEY) } catch { /* ignore */ }
        refetch()
      })
      .catch(() => {
        // Leave the key so a later load can retry; reset the guard for this session.
        applied.current = false
      })
  }, [tenantId, refetch])

  return null
}
